<template>
  <div>
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="w-12 h-12 border-b-2 rounded-full animate-spin border-custom-yellow"></div>
    </div> 
    <template v-else>
      <div v-if="!showPoolingView">
        <div class="relative mt-5 overflow-x-auto shadow-md sm:rounded-lg">
          <table class="w-full text-left text-gray-700 rtl:text-right">
            <thead>
              <tr class="bg-gray-100"> 
                <th scope="col" class="p-4">
                  <div class="flex items-center">
                    <input id="checkbox-all-search" type="checkbox"
                      class="w-4 h-4 bg-gray-100 border-gray-300 rounded text-custom-yellow focus:ring-custom-yellow"
                      :checked="allSelected"
                      @change="toggleSelectAll">
                    <label for="checkbox-all-search" class="sr-only">checkbox</label>
                  </div>
                </th>
                <th scope="col" class="px-6">Serial no.</th>
                <th scope="col" class="px-6">Blood Group</th>
                <th scope="col" class="px-6">Blood Product</th>
                <th scope="col" class="px-6">Volume</th>
                <th scope="col" class="px-6">Origin</th>
                <th scope="col" class="px-6">Donation Date (mm/dd/yyyy)</th>
                <th scope="col" class="px-6">Expiry Date (mm/dd/yyyy)</th>
                <th scope="col" class="px-6">Type</th>
                <th scope="col" class="px-6">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(product, index) in filteredBloodProducts" :key="product.product_id" class="border-b hover:bg-gray-50" :class="product.original_unit_serials ? 'bg-orange-50' : ''">
                <td class="w-4 p-4">
                  <div class="flex items-center">
                    <input :id="'checkbox-table-search-' + index" type="checkbox" class="w-4 h-4"
                      :checked="selectedProducts.includes(product.product_id)"
                      @change="toggleProductSelection(product.product_id)">
                    <label :for="'checkbox-table-search-' + index" class="sr-only">checkbox</label>
                  </div>
                </td>
                <td class="px-6 py-2">{{ product.pint_serial }}</td>
                <td class="px-6 py-2">{{ product.blood_group?.description }}</td>
                <td class="px-6 py-2">{{ product.unit_product?.name }}</td>
                <td class="px-6 py-2">{{ product.volume || '0' }} ml</td>
                <td class="px-6 py-2 text-sm">{{ product.source || product.blood_donation?.origin }}</td>
<td class="px-6 py-2">{{ product.blood_donation?.date_added ? new Date(product.blood_donation.date_added).toLocaleString() : 'User specified' }}</td>
            <td class="px-6 py-2">{{ product.expiry_date ? new Date(product.expiry_date).toLocaleString() : 'N/A' }}</td>
                <td class="px-6 py-2">
                  <span v-if="product.original_unit_serials" class="px-2 py-1 text-xs text-purple-700 bg-purple-100 rounded">
                    Pooled
                  </span>
                  <span v-else class="px-2 py-1 text-xs text-gray-600 bg-gray-100 rounded">
                    Regular
                  </span>
                </td>
                <td class="px-6 py-2 text-center">
                  <span class="p-2 rounded-lg min-w-[100px] text-center inline-block"
                    :class="{
                      'bg-green-50 text-green-500': ['Okay', 'Segregated'].includes(product.status),
                      'bg-red-50 text-red-500': ['Expired'].includes(product.status),
                      'bg-blue-50 text-blue-500': ['Assigned'].includes(product.status),
                      'bg-orange-50 text-orange-500': ['Issued'].includes(product.status) || product.facility_issuing,
                    }"
                  >
                    {{ product.facility_issuing ? `Issued to ${product.facility?.name || 'External Facility'}` : product.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <div class="flex items-center justify-center mt-4 space-x-2">
          <button @click="changePage(currentPage - 1)" :disabled="currentPage <= 1" 
            class="px-3 py-1 border rounded disabled:opacity-50">
            Previous
          </button>
          <span class="text-sm">Page {{ currentPage }} of {{ totalPages }}</span>
          <button @click="changePage(currentPage + 1)" :disabled="currentPage >= totalPages" 
            class="px-3 py-1 border rounded disabled:opacity-50">
            Next
          </button>
        </div>
      </div>
      
      <div v-else class="mt-5">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold">Blood Pooling</h3>
          <button @click="cancelPooling" class="text-gray-500 hover:text-gray-700">
            ← Back to list
          </button>
        </div>
        
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div class="p-4 bg-white border rounded-lg shadow-md">
            <h4 class="mb-3 font-semibold">Selected PLTS Units ({{ selectedProducts.length }})</h4>
            <div class="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table class="w-full text-left text-gray-700 text-md">
                <thead class="text-xs uppercase bg-gray-100">
                  <tr>
                    <th class="px-3 py-2">Serial No.</th>
                    <th class="px-3 py-2">Blood Group</th>
                    <th class="px-3 py-2">Volume (ml)</th>
                    <th class="px-3 py-2">Expiry Date (mm/dd/yyyy)</th>
                    <th class="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="product in selectedProductsData" :key="product.product_id" class="border-b hover:bg-gray-50">
                    <td class="px-3 py-2">{{ product.pint_serial }}</td>
                    <td class="px-3 py-2">{{ product.blood_group?.description }}</td>
                    <td class="px-3 py-2">{{ product.volume }}</td>
                    <td class="px-3 py-2">{{ new Date(product.expiry_date).toLocaleDateString() }}</td>
                    <td class="px-3 py-2">
                      <button @click="removeFromSelection(product.product_id)" class="text-orange-400 hover:text-orange-500">
                        <svg class="w-5 h-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                          fill="none" viewBox="0 0 24 24">
                          <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M5 7h14m-9 3v8m4-8v8M10 3h4a1 1 0 0 1 1 1v3H9V4a1 1 0 0 1 1-1ZM6 7h12v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7Z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="flex items-center justify-between pt-4 mt-4 border-t">
              <p class="font-semibold">Total Volume: {{ totalSelectedVolume }} ml</p>
              <p class="text-sm text-gray-500">Earliest Expiry: {{ earliestExpiryDate }}</p>
            </div>
          </div>
          
          <div class="p-4 bg-white border rounded-lg shadow-md">
            <h4 class="mb-3 font-semibold">New Pooled Product</h4>
            <form @submit.prevent="submitPooling">
              <div class="mb-4">
                <label class="block mb-1 text-sm font-medium text-gray-700">Serial Number <span class="text-red-500">*</span></label>
                <input v-model="pooledProduct.pint_serial" type="text" required
                  class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-custom-yellow">
              </div>
              <div class="mb-4">
                <label class="block mb-1 text-sm font-medium text-gray-700">Blood Group <span class="text-red-500">*</span></label>
                <input type="text" :value="selectedBloodGroupName" readonly
                  class="w-full px-3 py-2 text-gray-600 bg-gray-100 border rounded-md">
              </div>
              <div class="mb-4">
                <label class="block mb-1 text-sm font-medium text-gray-700">Volume (ml) <span class="text-red-500">*</span></label>
                <input v-model.number="pooledProduct.volume" type="number" readonly
                  class="w-full px-3 py-2 text-gray-600 bg-gray-100 border rounded-md">
              </div>
              <div class="mb-4">
                <label class="block mb-1 text-sm font-medium text-gray-700">Expiry Date (mm/dd/yyyy)<span class="text-red-500">*</span></label>
                <input v-model="pooledProduct.expiry_date" type="date" :max="minExpiryDate" required
                  class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-custom-yellow">
              </div>
              <button type="submit" :disabled="isSubmitting" 
                class="w-full px-4 py-2 text-white rounded bg-custom-yellow disabled:opacity-50">
                <span v-if="isSubmitting">Creating Pooled Product...</span>
                <span v-else>Submit Pooled Product</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService';

export default {
  name: 'BloodPoolingTab',
  props: {
    searchQuery: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 1,
      perPage: 50,
      loading: false,
      facility_code: null,
      products: [],
      bloodGroups: [],
      selectedProducts: [],
      showPoolingView: false,
      isSubmitting: false,
      pooledProduct: {
        pint_serial: '',
        blood_group_id: '',
        volume: 0,
        expiry_date: ''
      }
    };
  },
  computed: {
    filteredBloodProducts() {
      const search = this.searchQuery.toLowerCase();
      return this.products.filter(product => {
        const pintSerial = product.pint_serial || '';
        const matchesSearch = !search || pintSerial.toLowerCase().includes(search);
        return matchesSearch;
      });
    },
    allSelected() {
      return this.filteredBloodProducts.length > 0 && 
             this.filteredBloodProducts.every(p => this.selectedProducts.includes(p.product_id));
    },
    selectedProductsData() {
      return this.products.filter(p => this.selectedProducts.includes(p.product_id));
    },
    totalSelectedVolume() {
      return this.selectedProductsData.reduce((sum, p) => sum + (parseFloat(p.volume) || 0), 0);
    },
    earliestExpiryDate() {
      if (this.selectedProductsData.length === 0) return 'N/A';
      const dates = this.selectedProductsData
        .map(p => new Date(p.expiry_date))
        .filter(d => !isNaN(d.getTime()));
      if (dates.length === 0) return 'N/A';
      const earliest = new Date(Math.min(...dates));
      return earliest.toLocaleDateString();
    },
    minExpiryDate() {
      if (this.selectedProductsData.length === 0) return '';
      const dates = this.selectedProductsData
        .map(p => new Date(p.expiry_date))
        .filter(d => !isNaN(d.getTime()));
      if (dates.length === 0) return '';
      const earliest = new Date(Math.min(...dates));
      return earliest.toISOString().split('T')[0];
    },
    selectedBloodGroupName() {
      if (this.selectedProductsData.length === 0) return '';
      const bg = this.selectedProductsData[0].blood_group;
      return bg ? bg.description : '';
    }
  },
  created() {
    this.getBloodProducts();
    this.getBloodGroups();
  },
  methods: {
    async getBloodProducts() {
      this.loading = true;
      try {
        const queryParams = { 
          search_query: this.searchQuery, 
          page: this.currentPage, 
          per_page: this.perPage,
          unit_product_name: 'PLTS'
        };
        const response = await api.fetchFilteredProducts(queryParams);
        
        // Standard pagination response structure
        this.products = response.data || response;
        
        // Handle pagination - try to get from response or calculate
        if (response.current_page && response.total_pages) {
          this.currentPage = response.current_page;
          this.totalPages = response.total_pages;
        } else if (response.total_count) {
          this.currentPage = 1;
          this.totalPages = Math.ceil(response.total_count / this.perPage);
        } else {
          this.currentPage = 1;
          this.totalPages = Math.max(1, Math.ceil(this.products.length / this.perPage));
        }
      } catch (error) {
        console.error("Error fetching PLTS products:", error);
        this.products = [];
      } finally {
        this.loading = false;
      }
    },
    async getBloodGroups() {
      try {
        const response = await api.getBloodGroups();
        this.bloodGroups = response || [];
      } catch (error) {
        console.error("Error fetching blood groups:", error);
      }
    },
    changePage(page) {
      this.currentPage = page;
      this.getBloodProducts();
    },
    toggleSelectAll() {
      if (this.allSelected) {
        this.selectedProducts = [];
      } else {
        this.selectedProducts = this.filteredBloodProducts.map(p => p.product_id);
      }
    },
    toggleProductSelection(productId) {
      const index = this.selectedProducts.indexOf(productId);
      if (index === -1) {
        this.selectedProducts.push(productId);
      } else {
        this.selectedProducts.splice(index, 1);
      }
    },
    removeFromSelection(productId) {
      this.selectedProducts = this.selectedProducts.filter(id => id !== productId);
      
      if (this.selectedProductsData.length > 0) {
        this.pooledProduct.volume = this.totalSelectedVolume;
        const dates = this.selectedProductsData
          .map(p => new Date(p.expiry_date))
          .filter(d => !isNaN(d.getTime()));
        if (dates.length > 0) {
          const earliest = new Date(Math.min(...dates));
          this.pooledProduct.expiry_date = earliest.toISOString().split('T')[0];
        }
        this.pooledProduct.blood_group_id = this.selectedProductsData[0].blood_group_id;
      } else {
        this.pooledProduct.volume = 0;
        this.pooledProduct.expiry_date = '';
        this.pooledProduct.blood_group_id = '';
      }
    },
    initiatePooling() {
      if (this.selectedProductsData.length === 0) {
        SweetAlertService.showWarning('No Selection', 'Please select at least one PLTS unit to initiate pooling.');
        return;
      }
      
      const bloodGroupIds = [...new Set(this.selectedProductsData.map(p => p.blood_group_id))];
      if (bloodGroupIds.length > 1) {
        SweetAlertService.showWarning('Blood Group Mismatch', 'All selected units must have the same blood group for pooling.');
        return;
      }
      
      this.pooledProduct.volume = this.totalSelectedVolume;
      const dates = this.selectedProductsData
        .map(p => new Date(p.expiry_date))
        .filter(d => !isNaN(d.getTime()));
      if (dates.length > 0) {
        const earliest = new Date(Math.min(...dates));
        this.pooledProduct.expiry_date = earliest.toISOString().split('T')[0];
      }
      if (this.selectedProductsData.length > 0) {
        this.pooledProduct.blood_group_id = this.selectedProductsData[0].blood_group_id;
      }
      this.showPoolingView = true;
    },
    cancelPooling() {
      this.showPoolingView = false;
    },
    async submitPooling() {
      const volume = this.pooledProduct.volume;
      if (volume < 200 || volume > 450) {
        SweetAlertService.showWarning('Invalid Volume', 'Pooled product volume must be between 200ml and 450ml.');
        return;
      }
      
      this.isSubmitting = true;
      try {
        // Backend will calculate original_unit_serials from source units
        // It will combine: regular units' pint_serial + pooled units' existing original_unit_serials
        const selectedBloodGroupId = this.selectedProductsData[0]?.blood_group_id;
        const payload = {
          pint_serial: this.pooledProduct.pint_serial,
          blood_group_id: selectedBloodGroupId,
          volume: this.pooledProduct.volume,
          expiry_date: this.pooledProduct.expiry_date,
          source_unit_ids: this.selectedProducts,
          unit_product_name: 'PLTS'
        };
        
        await api.createPooledProduct(payload);
        
        await SweetAlertService.showSuccess('Success!', 'Pooled product created successfully!');
        
        this.selectedProducts = [];
        this.showPoolingView = false;
        this.pooledProduct = {
          pint_serial: '',
          blood_group_id: '',
          volume: 0,
          expiry_date: ''
        };
        
        this.getBloodProducts();
      } catch (error) {
        console.error("Error creating pooled product:", error);
        SweetAlertService.showError('Error', 'Failed to create pooled product.');
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
      this.getBloodProducts();
    }
  }
};
</script>